import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface CertificationRow {
  readonly certificationId: string;
  readonly certificationCode: string;
  readonly certificationName: string;
  readonly employeeRef: string | null;
  readonly issueDate: string | null;
  readonly expiryDate: string | null;
  readonly status: string;
}

const STATUSES = ['', 'ISSUED', 'ACTIVE', 'EXPIRED', 'REVOKED', 'RENEWED', 'SUSPENDED'];

/**
 * M35 — Certification Dashboard (§33). Register of issued certifications
 * (T473) carrying the issue and expiry dates and the §10 certification
 * lifecycle (ISSUED → RENEWED / REVOKED / EXPIRED). Administrators issue a new
 * certification to an employee — compliance certifications require the
 * prerequisite compliance training to be completed (BR-03). The list is
 * filterable by status, employee, an "expiring within N days" window and
 * free-text search. Tenant isolation is enforced server-side (LAW 4).
 */
export function CertificationDashboard(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CertificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [employeeRef, setEmployeeRef] = useState('');
  const [expiringWithinDays, setExpiringWithinDays] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newName, setNewName] = useState('');
  const [newEmployeeRef, setNewEmployeeRef] = useState('');
  const [newCompliance, setNewCompliance] = useState(false);
  const [newTrainingDone, setNewTrainingDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<CertificationRow | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (employeeRef) params.set('employeeRef', employeeRef);
    if (expiringWithinDays) params.set('expiringWithinDays', expiringWithinDays);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: CertificationRow[]; total: number }>(`/v1/kms/certifications${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, employeeRef, expiringWithinDays, q]);

  useEffect(() => load(), [load]);

  const issue = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!newName.trim()) {
      setActionError(t('m35.cert.nameRequired'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: CertificationRow }>(`/v1/kms/certifications`, {
        method: 'POST',
        body: {
          certificationName: newName.trim(),
          employeeRef: newEmployeeRef.trim() || undefined,
          isComplianceCertification: newCompliance,
          complianceTrainingCompleted: newTrainingDone,
        },
        token,
      });
      setCreated(r.data);
      setNewName('');
      setNewEmployeeRef('');
      setNewCompliance(false);
      setNewTrainingDone(false);
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.cert.notAuthorisedIssue'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="certification-dashboard">
      <h1>{t('m35.cert.title')}</h1>

      <form aria-label="issue-form" onSubmit={(e) => void issue(e)}>
        <label>
          {t('m35.cert.certName')}
          <input aria-label="field-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </label>
        <label>
          {t('m35.common.employee')}
          <input aria-label="field-employee" value={newEmployeeRef} onChange={(e) => setNewEmployeeRef(e.target.value)} placeholder={t('m35.cert.employeePh')} />
        </label>
        <label>
          {t('m35.cert.complianceCert')}
          <input aria-label="field-compliance" type="checkbox" checked={newCompliance} onChange={(e) => setNewCompliance(e.target.checked)} />
        </label>
        <label>
          {t('m35.cert.trainingCompleted')}
          <input aria-label="field-trainingdone" type="checkbox" checked={newTrainingDone} onChange={(e) => setNewTrainingDone(e.target.checked)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-issue" disabled={busy}>{busy ? t('m35.cert.issuing') : t('m35.cert.issueBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="certification-action-error">{actionError}</p>}
      {created && (
        <dl role="status" aria-label="issue-result">
          <dt>{t('m35.cert.resultCode')}</dt><dd aria-label="result-code">{created.certificationCode}</dd>
          <dt>{t('m35.cert.resultName')}</dt><dd aria-label="result-name">{created.certificationName}</dd>
          <dt>{t('m35.common.status')}</dt><dd aria-label="result-status">{created.status}</dd>
        </dl>
      )}

      <div className="filters">
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.employee')}
          <input aria-label="filter-employee" value={employeeRef} onChange={(e) => setEmployeeRef(e.target.value)} placeholder={t('m35.cert.employeePh')} />
        </label>
        <label>
          {t('m35.cert.expiringWithin')}
          <input aria-label="filter-expiring" type="number" value={expiringWithinDays} onChange={(e) => setExpiringWithinDays(e.target.value)} />
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.cert.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="certification-count">{t('m35.cert.count', { n: total })}</p>
          <DataTable<CertificationRow>
            ariaLabel="certification-table"
            testId="certification-table"
            rowKey={(r) => r.certificationId}
            rowDataAttrs={(r) => ({ 'data-status': r.status })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.certificationCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.certificationName },
              { key: 'employee', header: t('m35.common.employee'), render: (r) => r.employeeRef ?? '—', cellAriaLabel: (r) => `cert-employee-${r.certificationCode}` },
              { key: 'issued', header: t('m35.cert.colIssued'), render: (r) => day(r.issueDate) },
              { key: 'expires', header: t('m35.cert.colExpires'), render: (r) => day(r.expiryDate), cellAriaLabel: (r) => `cert-expiry-${r.certificationCode}` },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
            ]}
          />
        </>
      )}
    </main>
  );
}
